import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, FilterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ProjectCard from '../../components/ui/ProjectCard';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import SideModal from '../../components/shared/SideModal/SideModal';

interface Project {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  lastDeploy: string;
  type: string;
  url?: string;
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'running' | 'stopped' | 'deploying' | 'error' | 'all'>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Mock data - Proyectos
  const [projects] = useState<Project[]>([
    {
      id: '1',
      name: 'E-commerce Platform',
      status: 'running',
      lastDeploy: '2 hours ago',
      type: 'Web Application',
      url: 'https://ecommerce.kubiy.cloud',
    },
    {
      id: '2',
      name: 'API Gateway',
      status: 'running',
      lastDeploy: '1 day ago',
      type: 'API Service',
      url: 'https://api.kubiy.cloud',
    },
    {
      id: '3',
      name: 'Mobile App Backend',
      status: 'deploying',
      lastDeploy: 'Just now',
      type: 'Backend Service',
    },
    {
      id: '4',
      name: 'Analytics Dashboard',
      status: 'stopped',
      lastDeploy: '3 days ago',
      type: 'Web Application',
    },
    {
      id: '5',
      name: 'Microservices Cluster',
      status: 'running',
      lastDeploy: '5 hours ago',
      type: 'Container Service',
    },
    {
      id: '6',
      name: 'Static Website',
      status: 'error',
      lastDeploy: '1 week ago',
      type: 'Static Site',
    },
  ]);

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = 
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.url && project.url.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const matchesType = filterType === 'all' || project.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const projectTypes = Array.from(new Set(projects.map(p => p.type)));

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Projects</h1>
              <button
                onClick={() => setShowHelpModal(true)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title="Get help about Projects"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Organize Your Infrastructure</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              Everything related to one product, client, or environment lives here. Projects help you organize and manage your apps, sites, databases, and servers in a centralized way.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ReloadOutlined />
              Refresh
            </button>
            <button
              onClick={() => navigate('/projects/create')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <PlusOutlined />
              New Project
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, type or URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
            >
              <option value="all">All Types</option>
              {projectTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="deploying">Deploying</option>
            <option value="error">Error</option>
          </select>

          {/* View Mode */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      {filteredProjects.length === 0 ? (
        <EmptyState
          title="No projects found"
          description={searchTerm || filterStatus !== 'all' || filterType !== 'all' 
            ? "Try adjusting your search or filters to find what you're looking for."
            : "Get started by creating your first project."}
          action={!searchTerm && filterStatus === 'all' && filterType === 'all' ? {
            label: 'Create New Project',
            onClick: () => navigate('/projects/create')
          } : undefined}
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  name={project.name}
                  status={project.status}
                  lastDeploy={project.lastDeploy}
                  type={project.type}
                  url={project.url}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProjects.map((project) => (
                <div key={project.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <ProjectCard
                    name={project.name}
                    status={project.status}
                    lastDeploy={project.lastDeploy}
                    type={project.type}
                    url={project.url}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Summary Stats */}
      {filteredProjects.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Projects</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{projects.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Running</div>
            <div className="text-2xl font-bold text-green-600">
              {projects.filter(p => p.status === 'running').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Deploying</div>
            <div className="text-2xl font-bold text-blue-600">
              {projects.filter(p => p.status === 'deploying').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Stopped</div>
            <div className="text-2xl font-bold text-gray-600">
              {projects.filter(p => p.status === 'stopped').length}
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="About Projects"
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">What are Projects?</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Projects are containers that help you organize and manage all your infrastructure resources in one place. 
              Everything related to a single product, client, or environment can be grouped together within a project.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">What can you do with Projects?</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Organize apps, sites, databases, and servers by client or product</li>
              <li>Manage resources that belong to the same environment</li>
              <li>Track costs and usage per project</li>
              <li>Set up team access and permissions at the project level</li>
              <li>Monitor all resources in a centralized dashboard</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Getting Started</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              To create a new project, click on the "New Project" button. You'll be able to choose what type of resource
              you want to create: Deploy App, App Server, Database, or Virtual Machine.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Once created, you can add multiple resources to the same project, making it easier to manage related
              infrastructure components together.
            </p>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default Projects;